import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, HeartIcon, UserGroupIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';
import { getImageUrl } from '../../utils/imageUtils';

interface ClinicBranding {
  CLINIC_NAME?: string;
  CLINIC_LOGO?: string;
  CLINIC_TAGLINE?: string;
}

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [clinicBranding, setClinicBranding] = useState<ClinicBranding>({});
  const [brandingLoading, setBrandingLoading] = useState(true);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  // Load saved credentials on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';
    
    if (savedEmail && savedPassword && savedRememberMe) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  // Fetch clinic branding on component mount
  useEffect(() => {
    const fetchClinicBranding = async () => {
      try {
        const response = await adminAPI.getClinicBranding();
        const branding = response.data.data || {};
        setClinicBranding(branding);
      } catch (error: any) {
        console.warn('Failed to fetch clinic branding for login page:', error);
        
        // Log detailed error for debugging
        if (error.response) {
          console.warn('Branding fetch error details:', {
            status: error.response.status,
            message: error.response.data?.message,
            url: error.config?.url
          });
        }
        
        // Use default values if fetch fails - don't show error to user on login page
        setClinicBranding({
          CLINIC_NAME: 'MedCare Pro',
          CLINIC_TAGLINE: 'Healthcare Management System'
        });
      } finally {
        setBrandingLoading(false);
      }
    };

    fetchClinicBranding();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      await login(email, password);
      
      // Save credentials if "Remember me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('rememberedPassword', password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        // Clear saved credentials if "Remember me" is unchecked
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberMe');
      }
      
      // Get user role from localStorage to determine redirect
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.role === 'DOCTOR') {
          navigate('/workflow/triage');
        } else if (user.role === 'PHARMACIST') {
          navigate('/pharmacy');
        } else if (user.role === 'LAB_TECHNICIAN') {
          navigate('/laboratory');
        } else if (user.role === 'RECEPTIONIST') {
          navigate('/patients');
        } else if (user.role === 'ADMIN') {
          navigate('/patients');
        } else if (user.role === 'REPORTS') {
          navigate('/reports/doctors');
        } else {
          navigate('/dashboard');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Login failed');
    }
  };


  return (
    <div className="min-h-screen flex relative" style={{ background: '#FAFBFC' }}>
      {/* Background Image - Full Page */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/health-still-life-with-copy-space.jpg)',
          opacity: 0.15,
          zIndex: 0
        }}
      ></div>
      
      {/* Overlay for better readability */}
      <div 
        className="fixed inset-0"
        style={{
          background: 'rgba(250, 251, 252, 0.7)',
          zIndex: 1
        }}
      ></div>
      
      {/* Left side - Brand Section */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 overflow-hidden">
        <div className="flex flex-col justify-center items-center w-full px-6 sm:px-8 lg:px-12 min-h-screen">
          <div className="w-full max-w-md mx-auto flex flex-col items-center">
            <div className="mb-6 sm:mb-8 cursor-pointer" onClick={() => navigate('/dashboard')}>
              {brandingLoading ? (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex items-center justify-center" style={{ background: 'rgba(82, 183, 136, 0.1)' }}>
                  <div className="w-6 h-6 border-2 border-transparent border-t-current rounded-full animate-spin" style={{ color: '#52B788' }}></div>
                </div>
              ) : clinicBranding.CLINIC_LOGO ? (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border mx-auto" style={{ borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                  <img 
                    src={getImageUrl(clinicBranding.CLINIC_LOGO) || ''} 
                    alt="Clinic Logo" 
                    className="w-full h-full object-contain p-3"
                    onError={(e) => {
                      console.error('Failed to load logo:', clinicBranding.CLINIC_LOGO, e);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg flex items-center justify-center border mx-auto" style={{ background: '#52B788', borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                  <HeartIcon className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                </div>
              )}
            </div>
            
            <div className="mb-6 sm:mb-8 text-center w-full">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2" style={{ color: '#52B788' }}>
                {clinicBranding.CLINIC_NAME || 'Global Pharma Medical Center'}
              </h1>
              <p className="text-xs sm:text-sm" style={{ color: '#5A5A5A' }}>
                {clinicBranding.CLINIC_TAGLINE || 'Healthcare Management System'}
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-3 w-full">
              <div className="flex items-center space-x-3 bg-white rounded-lg p-3 sm:p-4 border" style={{ borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 183, 136, 0.1)' }}>
                  <UserGroupIcon className="w-5 h-5" style={{ color: '#52B788' }} />
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: '#1A1A1A' }}>Smart Patient Management</span>
              </div>
              <div className="flex items-center space-x-3 bg-white rounded-lg p-3 sm:p-4 border" style={{ borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 183, 136, 0.1)' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#52B788' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: '#1A1A1A' }}>Secure & Compliant Platform</span>
              </div>
              <div className="flex items-center space-x-3 bg-white rounded-lg p-3 sm:p-4 border" style={{ borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(82, 183, 136, 0.1)' }}>
                  <DocumentTextIcon className="w-5 h-5" style={{ color: '#52B788' }} />
                </div>
                <span className="text-xs sm:text-sm font-medium" style={{ color: '#1A1A1A' }}>Advanced Analytics & Reports</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-md w-full space-y-6">
          {/* Mobile brand header */}
          <div className="lg:hidden text-center mb-6">
            {brandingLoading ? (
              <div className="mx-auto w-16 h-16 rounded-lg flex items-center justify-center mb-4 border" style={{ background: 'rgba(82, 183, 136, 0.1)', borderColor: '#F0F1F3' }}>
                <div className="w-6 h-6 border-2 border-transparent border-t-current rounded-full animate-spin" style={{ color: '#52B788' }}></div>
              </div>
            ) : clinicBranding.CLINIC_LOGO ? (
              <div className="mx-auto w-16 h-16 rounded-lg overflow-hidden mb-4 border" style={{ borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                <img 
                  src={getImageUrl(clinicBranding.CLINIC_LOGO) || ''} 
                  alt="Clinic Logo" 
                  className="w-full h-full object-contain p-2"
                />
              </div>
            ) : (
              <div className="mx-auto w-16 h-16 rounded-lg flex items-center justify-center mb-4 border" style={{ background: '#52B788', borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
                <HeartIcon className="w-10 h-10 text-white" />
              </div>
            )}
            <h1 className="text-2xl font-bold mb-2" style={{ color: '#52B788' }}>
              {clinicBranding.CLINIC_NAME || 'MedCare Pro'}
            </h1>
            <p className="text-sm" style={{ color: '#5A5A5A' }}>
              {clinicBranding.CLINIC_TAGLINE || 'Healthcare Management System'}
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-lg border p-6" style={{ borderColor: '#F0F1F3', boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04)' }}>
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold mb-1" style={{ color: '#1A1A1A' }}>Welcome Back</h2>
              <p className="text-xs" style={{ color: '#5A5A5A' }}>Sign in to access your dashboard</p>
            </div>

            {error && (
              <div className="mb-4 p-3 border rounded-lg" style={{ borderColor: '#EF4444', background: 'rgba(239, 68, 68, 0.05)' }}>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" style={{ color: '#EF4444' }}>
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-2">
                    <p className="text-xs font-medium" style={{ color: '#EF4444' }}>{error}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-xs font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Email Address
                </label>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border rounded-md"
                    style={{ 
                      borderColor: '#F0F1F3', 
                      background: '#FFFFFF',
                      color: '#1A1A1A'
                    }}
                    placeholder="Enter your email"
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#52B788';
                      e.currentTarget.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#F0F1F3';
                    }}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#5A5A5A' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 text-sm border rounded-md"
                    style={{ 
                      borderColor: '#F0F1F3', 
                      background: '#FFFFFF',
                      color: '#1A1A1A'
                    }}
                    placeholder="Enter your password"
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#52B788';
                      e.currentTarget.style.outline = 'none';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#F0F1F3';
                    }}
                  />
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#5A5A5A' }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ color: '#5A5A5A' }}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4" />
                    ) : (
                      <EyeIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-3 w-3 rounded"
                  style={{ 
                    accentColor: '#52B788',
                    borderColor: '#F0F1F3'
                  }}
                />
                <label htmlFor="remember-me" className="ml-2 block text-xs" style={{ color: '#5A5A5A' }}>
                  Remember me
                </label>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-2.5 px-4 rounded-md text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ 
                    background: '#52B788',
                    color: '#FFFFFF'
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) e.currentTarget.style.background = '#3A9B6F';
                  }}
                  onMouseLeave={(e) => {
                    if (!isLoading) e.currentTarget.style.background = '#52B788';
                  }}
                >
                  {isLoading ? 'Authenticating...' : 'Sign In'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;